// All by date: every saved reel, newest first, in the same buckets as Albums → Timeline
// (this week · earlier this month · each month · before that), with filters and live progress.
import type { ComponentChildren } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { api, ApiError } from '../api';
import { Link, navigate } from '../router';
import { Back, Chevron, Plus, SearchIcon, VegMark } from '../icons';
import { BottomNav, ErrorBox, useToast } from '../components/Shell';
import { RecipeCard } from '../components/RecipeCard';
import { SwipeToGrocery } from '../components/Swipe';
import { AddReelSheet } from '../components/AddReelSheet';
import { useScript } from '../script';
import { monthName, t as tr, useT } from '../i18n';
import { load, save } from '../storage';
import type { SavedReel, Script } from '../types';

type Filter = 'all' | 'veg' | 'nonveg' | 'untried' | `tag:${string}`;
const CACHE = 'er.cache.reels';

/** The bucket a save falls in; ids match the anchors Albums → Timeline links to. */
function bucketOf(iso: string, now = new Date()): { id: string; label: string } {
  const d = new Date(iso);
  const weekAgo = new Date(now.getTime() - 7 * 86400e3);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const oldest = new Date(now.getFullYear(), now.getMonth() - 6, 1);
  if (d > weekAgo) return { id: 'week', label: tr('tl.week') };
  if (d >= monthStart) return { id: 'month_rest', label: tr('tl.month_rest') };
  if (d >= oldest) {
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    return { id: `month${ym}`, label: monthName(ym) };
  }
  return { id: 'before', label: tr('tl.before') };
}

export function Timeline() {
  const t = useT();
  const [reels, setReels] = useState<SavedReel[] | null>(() => load<SavedReel[] | null>(CACHE, null));
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [adding, setAdding] = useState(false);
  const [retrying, setRetrying] = useState<number | null>(null);
  const [script, setScript] = useScript();
  const [toast, showToast] = useToast();

  const refresh = () =>
    api.reels().then((r) => { setReels(r); save(CACHE, r); setErr(''); }).catch((e: ApiError) => {
      if (e.status !== 401) setErr(e.message);
    });

  useEffect(() => { refresh(); }, []);
  const busy = reels?.some((r) => r.status === 'queued' || r.status === 'processing');
  useEffect(() => {
    if (!busy) return;
    const id = setInterval(refresh, 4000);
    return () => clearInterval(id);
  }, [busy]);
  // Jump to the month the user tapped in Albums → Timeline once the list is there.
  useEffect(() => {
    if (reels && location.hash) document.getElementById(location.hash.slice(1))?.scrollIntoView();
  }, [reels !== null]);

  const topTags = useMemo(() => {
    const counts = new Map<string, number>();
    reels?.forEach((r) => r.tags?.forEach((tag) => counts.set(tag, (counts.get(tag) ?? 0) + 1)));
    return [...counts].filter(([, n]) => n > 1).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([tag]) => tag);
  }, [reels]);

  const shown = (reels ?? []).filter((r) => {
    switch (filter) {
      case 'all': return true;
      case 'veg': return r.is_veg === true;
      case 'nonveg': return r.is_veg === false;
      case 'untried': return r.status === 'ready' && r.cooked_count === 0 && r.ingredient_count > 0;
      default: return r.tags?.includes(filter.slice(4));
    }
  });

  const retry = async (id: number) => {
    setRetrying(id);
    try {
      await api.retry(id);
      await refresh();
      showToast(t('timeline.retrying'));
    } catch (e) {
      showToast((e as ApiError).message);
    } finally {
      setRetrying(null);
    }
  };

  const cycleScript = () => {
    const order: Script[] = ['ta', 'en', 'tg'];
    setScript(order[(order.indexOf(script) + 1) % order.length]);
  };

  const chip = (f: Filter, label: ComponentChildren) => (
    <button class="chip" aria-pressed={filter === f} onClick={() => setFilter(filter === f && f !== 'all' ? 'all' : f)}>{label}</button>
  );

  let lastBucket = '';
  return (
    <div class="page with-nav">
      <div class="backbar">
        <Link href="/albums?view=timeline" class="icon-btn" aria-label={t('album.backToAlbums')}><Back /></Link>
        <button class="chip" style="height:44px" onClick={cycleScript} aria-label={t('timeline.scriptHint', { script: t(`script.${script}`) })}>
          <span>{t(`script.${script}`)}</span><Chevron />
        </button>
      </div>
      <div class="pad" style="padding-top:4px;display:flex;align-items:baseline;justify-content:space-between">
        <h1 class="h1" style="font-size:30px">{t('timeline.title')}</h1>
        <span class="small muted">{t('timeline.newest')}</span>
      </div>

      <div class="pad" style="padding-top:12px;display:flex;flex-direction:column;gap:12px">
        <form role="search" onSubmit={(e) => { e.preventDefault(); const q = (e.currentTarget.elements.namedItem('q') as HTMLInputElement).value.trim(); if (q) navigate(`/search?q=${encodeURIComponent(q)}`); }}>
          <label class="field">
            <SearchIcon />
            <input name="q" type="search" enterKeyHint="search" placeholder={t('search.placeholder')} aria-label={t('timeline.search')} autoComplete="off" />
          </label>
        </form>
        <div class="chips" role="group" aria-label={t('filter.label')}>
          {chip('all', `${t('filter.all')} · ${reels?.length ?? 0}`)}
          {chip('veg', <><VegMark veg={true} size={13} />{t('common.veg')}</>)}
          {chip('nonveg', <><VegMark veg={false} size={13} />{t('common.nonveg')}</>)}
          {chip('untried', t('filter.untried'))}
          {topTags.map((tag) => chip(`tag:${tag}`, tag))}
        </div>
      </div>

      {err && <ErrorBox message={err} onRetry={refresh} />}

      <div class="pad list" style="padding-top:8px;padding-bottom:96px">
        {reels === null && !err && [1, 2, 3].map(() => <div class="skeleton" style="height:120px" aria-hidden="true" />)}
        {reels?.length === 0 && (
          <div class="card" style="padding:20px;display:flex;flex-direction:column;gap:12px">
            <div class="display" style="font-weight:700;font-size:20px">{t('timeline.empty')}</div>
            <div class="muted" style="line-height:1.6">{t('timeline.emptyBody')}</div>
            <button class="btn btn-primary" onClick={() => setAdding(true)}>{t('home.pasteLink')}</button>
          </div>
        )}
        {reels && reels.length > 0 && shown.length === 0 && (
          <div class="hint-box">{t('timeline.noMatch')} <button class="btn-text" style="padding:0;min-height:0;color:var(--accent-text)" onClick={() => setFilter('all')}>{t('timeline.showAll')}</button></div>
        )}
        {shown.map((r) => {
          const b = bucketOf(r.saved_at);
          const head = b.id !== lastBucket ? <h2 id={b.id} class="section-label" style="margin:14px 4px 0;scroll-margin-top:16px">{b.label}</h2> : null;
          lastBucket = b.id;
          const card = <RecipeCard r={r} script={script} onRetry={retry} retrying={retrying === r.id} />;
          return <>{head}{r.status === 'ready' && r.ingredient_count > 0 ? <SwipeToGrocery key={r.id} id={r.id} onToast={showToast}>{card}</SwipeToGrocery> : card}</>;
        })}
      </div>

      <button class="fab" onClick={() => setAdding(true)}><Plus />{t('common.addReel')}</button>
      {adding && <AddReelSheet onClose={() => setAdding(false)} onAdded={() => { setAdding(false); refresh(); showToast(t('add.added')); }} />}
      {toast}
      <BottomNav current="albums" />
    </div>
  );
}
